import React, { useEffect, useMemo, useState } from "react";
import { Button, Icon, Loader, Message, Radio, Table } from "semantic-ui-react";
export function PublishedVersionsSection({ formKey, configClient, refreshKey, onConfigRecordChange, }) {
    const [collapsed, setCollapsed] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeVersionId, setActiveVersionId] = useState(undefined);
    const [versions, setVersions] = useState([]);
    const sortedVersions = useMemo(() => {
        return [...versions].sort((a, b) => b.createdAt - a.createdAt);
    }, [versions]);
    const loadVersions = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const data = await configClient.listVersions(formKey);
            setActiveVersionId(data.activeVersionId);
            setVersions(data.versions || []);
        }
        catch (e) {
            setError("Failed to load versions");
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!collapsed) {
            loadVersions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapsed, refreshKey, formKey]);
    const setActive = async (versionId) => {
        const ok = window.confirm("Set this version as the active published version? This affects what users will see.");
        if (!ok)
            return;
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const res = await configClient.activateVersion(formKey, versionId);
            setActiveVersionId(res.activeVersionId);
            setSuccess(`Active version set to ${versionId}`);
        }
        catch (e) {
            setError("Failed to set active version");
        }
        finally {
            setLoading(false);
        }
    };
    const loadToDraft = async (version) => {
        const ok = window.confirm("Warning: This will overwrite your current draft configuration with the selected published version. Continue?");
        if (!ok)
            return;
        setError(null);
        setSuccess(null);
        // Frontend-only: apply the published snapshot into the draft state.
        // The builder autosave will persist it as part of the normal draft editing flow.
        onConfigRecordChange?.({
            formConfig: version.formConfig,
            actionsConfig: version.actionsConfig ?? {},
            styleConfig: version.styleConfig ?? {},
        });
        setSuccess(`Loaded version ${version.versionId} into draft (autosave will persist)`);
    };
    return (React.createElement("div", { style: { marginTop: "1em" } },
        React.createElement(Button, { basic: true, size: "small", loading: loading, onClick: async () => {
                const nextCollapsed = !collapsed;
                setCollapsed(nextCollapsed);
                if (!nextCollapsed) {
                    // @TODO: FLU-193 Handle this.
                    await loadVersions();
                }
            } },
            React.createElement(Icon, { name: collapsed ? "chevron down" : "chevron up" }),
            "Versions"),
        !collapsed && (React.createElement("div", { style: { marginTop: "1em" } },
            (error || success) && (React.createElement(Message, { negative: !!error, positive: !!success },
                React.createElement(Message.Header, null, error ? "Error" : "Success"),
                React.createElement("p", null, error || success))),
            React.createElement("div", { style: { color: "#666", fontSize: "0.9em", marginBottom: "0.75em" } },
                "Manage published versions. The ",
                React.createElement("strong", null, "active"),
                " version is what users see."),
            loading && (React.createElement("div", { style: { marginBottom: "0.75em" } },
                React.createElement(Loader, { active: true, inline: true, size: "small" }),
                " Loading versions...")),
            React.createElement(Table, { celled: true, compact: true, striped: true },
                React.createElement(Table.Header, null,
                    React.createElement(Table.Row, null,
                        React.createElement(Table.HeaderCell, null, "Version"),
                        React.createElement(Table.HeaderCell, null, "Published at"),
                        React.createElement(Table.HeaderCell, null, "Published by"),
                        React.createElement(Table.HeaderCell, null, "Active"),
                        React.createElement(Table.HeaderCell, { textAlign: "right" }, "Actions"))),
                React.createElement(Table.Body, null, sortedVersions.length === 0 ? (React.createElement(Table.Row, null,
                    React.createElement(Table.Cell, { colSpan: "5" }, "No published versions yet. Publish first."))) : (sortedVersions.map((v) => {
                    const isActive = !!activeVersionId && v.versionId === activeVersionId;
                    return (React.createElement(Table.Row, { key: v.versionId, positive: isActive },
                        React.createElement(Table.Cell, null,
                            React.createElement("code", null, v.versionId?.slice(0, 5))),
                        React.createElement(Table.Cell, null, new Date(v.createdAt).toLocaleString()),
                        React.createElement(Table.Cell, null, v.publishedBy || "-"),
                        React.createElement(Table.Cell, { collapsing: true },
                            React.createElement(Radio, { checked: isActive, disabled: loading, onChange: () => {
                                    if (!isActive)
                                        setActive(v.versionId);
                                } })),
                        React.createElement(Table.Cell, { textAlign: "right" },
                            React.createElement(Button, { size: "tiny", basic: true, disabled: loading, onClick: () => loadToDraft(v) }, "Load"))));
                }))))))));
}
